import { useFilters } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { PITCH_VIS_FILTER_KEYS, TEAM_PITCH_VIS_FILTER_SOURCE } from '@/analytics/snowplow/filterTracking';
import { useTrackFilterChanges } from '@/analytics/snowplow/hooks/useTrackFilterChanges';
import { unwrappedEventsDataDefaultColumnsAtom } from '@/atoms/dataLocker/events/table';
import { isMetricMappedAtom, metricFilterMappingEnabledAtom } from '@/atoms/metric/metricFilterMapping';
import { teamEventMetricsAtom } from '@/atoms/metric/teamMetrics';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { teamEventMetricFiltersAtom, teamEventsForMetricCountAtom } from '@/atoms/queries/team/teamEventData';
import { unwrappedSelectedTeamNameAtom } from '@/atoms/team/info';
import { teamIdAtom } from '@/atoms/team/team';
import { EventDataControls } from '@/components/controls/EventsDataControls';
import { PageTitle } from '@/components/head/PageTitle';
import { TeamLayoutWithFilters } from '@/components/layout/team/TeamLayoutWithFilters';
import { VisSectionContainer } from '@/components/layout/VisSectionContainer';
import { HydrateMetricFilterMapping } from '@/components/metric/HydrateMetricFilterMapping';
import { MetricKeyDropdown } from '@/components/metric/MetricKeyDropdown';
import { VisDisplaySection } from '@/components/section/VisDisplaySection';
import { PitchVisSettingsPanel } from '@/components/settings/PitchVisSettingsPanel';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { TeamEventInsights } from '@/components/team/vis/TeamEventInsights';
import { TeamVisEventsTable } from '@/components/team/vis/TeamVisEventsTable';
import { EventCountInfoBar } from '@/components/visualisations/EventCountInfoBar';
import { PitchVisualisation } from '@/components/visualisations/PitchVisualisation/PitchVisualisation';
import { EVENTS_TABLE_COLUMN_ENTITIES } from '@/consts/dataLocker/events';
import { teamPitchVisComponents } from '@/consts/visualisationComponents';
import { ALL_EVENTS_METRIC_KEY } from '@/consts/visualisations';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useApplyMetricFilterMapping } from '@/hooks/useApplyMetricFilterMapping/useApplyMetricFilterMapping';
import { useEntityPageTitle } from '@/hooks/useEntityPageTitle';
import { useInitPitchDisplayPreset } from '@/hooks/useInitPitchDisplayPreset/useInitPitchDisplayPreset';
import { useIsEventInsightsVisible } from '@/hooks/useIsEventInsightsVisible';
import { useMetricPitchPresetNavigation } from '@/hooks/useMetricPitchPresetNavigation/useMetricPitchPresetNavigation';
import { useTableColumns } from '@/hooks/useTableColumns';
import { visEventsUrl } from '@/query/url';
import { TableId } from '@/types/table';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const [{ metric: metricKey }] = useFilters('main');
  const teamId = useAtomValue(teamIdAtom);
  const teamEventMetricFilters = useAtomValue(teamEventMetricFiltersAtom);
  const isMetricMapped = useAtomValue(isMetricMappedAtom);
  useCsvDownload(
    pageTitle,
    visEventsUrl('team', teamId, metricKey, 'events', { ...teamEventMetricFilters }, isMetricMapped),
  );
  return null;
};

const VisDisplaySectionWithControls = ({ showInsights }: { showInsights: boolean }) => {
  useTableColumns(EVENTS_TABLE_COLUMN_ENTITIES);
  useApplyMetricFilterMapping();
  const [{ metric: selectedMetric }] = useFilters('main');
  const isMappingEnabled = useAtomValue(metricFilterMappingEnabledAtom);
  const pitchPresetRedirect = useInitPitchDisplayPreset();
  const onMetricChange = useMetricPitchPresetNavigation();

  if (pitchPresetRedirect) return pitchPresetRedirect;

  const metricKeyDropdownComponent = (
    <MetricKeyDropdown
      metricDataAtom={teamEventMetricsAtom}
      // the tab links drop the metric from the URL; with the mapping on, come back to the metric last selected
      initialMetricKey={(isMappingEnabled && selectedMetric) || ALL_EVENTS_METRIC_KEY}
      onMetricChange={onMetricChange}
    />
  );

  return (
    <VisSectionContainer>
      <EventDataControls metricKeyDropdownComponent={metricKeyDropdownComponent} />
      <VisDisplaySection
        rowBreakpoint="none"
        EventsTableComponent={TeamVisEventsTable}
        visualisationComponent={
          <PitchVisualisation
            visualisations={teamPitchVisComponents}
            headerComponent={metricKeyDropdownComponent}
            footerComponent={<EventCountInfoBar eventCountAtom={teamEventsForMetricCountAtom} />}
          />
        }
        insightComponent={showInsights ? <TeamEventInsights /> : undefined}
      />
    </VisSectionContainer>
  );
};

export const TeamVisPage = () => {
  const { t } = useTranslation('navigation');
  const pageTitle = useEntityPageTitle(t('secondary.pitchVis'), useAtomValue(unwrappedSelectedTeamNameAtom));
  useTrackFilterChanges(PITCH_VIS_FILTER_KEYS, TEAM_PITCH_VIS_FILTER_SOURCE);
  const showInsights = useIsEventInsightsVisible();

  return (
    <ScopeProvider atoms={[tableColumnEntitiesAtom, metricFilterMappingEnabledAtom]}>
      <HydrateMetricFilterMapping>
        <PageTitle>{pageTitle}</PageTitle>
        <TeamLayoutWithFilters
          settingsPanel={<PitchVisSettingsPanel />}
          insightComponent={showInsights ? <TeamEventInsights /> : undefined}
        >
          <ScopedTableWithDefaultColumns
            defaultColumnsAtom={unwrappedEventsDataDefaultColumnsAtom}
            tableId={TableId.TEAM_EVENTS}
          >
            <CsvDownload pageTitle={pageTitle} />
            <VisDisplaySectionWithControls showInsights={showInsights} />
          </ScopedTableWithDefaultColumns>
        </TeamLayoutWithFilters>
      </HydrateMetricFilterMapping>
    </ScopeProvider>
  );
};
